import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, Megaphone, Users, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

export const AdminMessages = () => {
  const { user: currentUser } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;
  const labels = currentUser?.labels || { people: 'Kullanıcılar', payment: 'Ödeme', identifier: 'Numara' };

  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [announcementMsg, setAnnouncementMsg] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendSmsDuyuru, setSendSmsDuyuru] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
      setUsers(data.filter((u) => u.role === 'user'));
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi');
    }
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((u) => u.id));
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) { toast.error('Mesaj yazın'); return; }
    if (selectedUsers.length === 0) { toast.error('En az bir kişi seçin'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/messages/send`, {
        user_ids: selectedUsers,
        message: message.trim(),
        send_sms: sendSms,
      }, { withCredentials: true });
      toast.success(`${data.sent} kişiye mesaj gönderildi${data.sms_sent > 0 ? `, ${data.sms_sent} SMS` : ''}`);
      setMessage('');
      setSelectedUsers([]);
      setSendSms(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gönderim başarısız');
    } finally {
      setSending(false);
    }
  };

  const handleSendAnnouncement = async () => {
    if (!announcementMsg.trim()) { toast.error('Duyuru mesajı yazın'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/messages/announcement`, {
        message: announcementMsg.trim(),
        send_sms: sendSmsDuyuru,
      }, { withCredentials: true });
      toast.success(`Duyuru ${data.sent} kişiye gönderildi${data.sms_sent > 0 ? `, ${data.sms_sent} SMS` : ''}`);
      setAnnouncementMsg('');
      setSendSmsDuyuru(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gönderim başarısız');
    } finally {
      setSending(false);
    }
  };

  return (
    <PageLayout>
      
      
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="messages-page-title">
              Mesajlar & Duyurular
            </h1>
            <p className="text-base text-[#595959] mt-2">
              {labels.people} ile iletişim kurun
            </p>
          </div>

          <Tabs defaultValue="message" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 h-12" data-testid="message-tabs">
              <TabsTrigger value="message" className="gap-2 text-sm" data-testid="tab-message">
                <MessageSquare className="w-4 h-4" />
                Kişiye Mesaj
              </TabsTrigger>
              <TabsTrigger value="announcement" className="gap-2 text-sm" data-testid="tab-announcement">
                <Megaphone className="w-4 h-4" />
                Genel Duyuru
              </TabsTrigger>
            </TabsList>

            {/* Direct Message Tab */}
            <TabsContent value="message" className="space-y-4">
              <Card className="border border-[#E5E5E5]" data-testid="select-users-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-[#2563EB] flex items-center gap-2">
                      <Users className="w-5 h-5" strokeWidth={1.5} />
                      {labels.people} Seçin
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-button">
                      {selectedUsers.length === users.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {users.length === 0 ? (
                    <p className="text-[#595959] text-sm">Henüz kayıtlı {labels.people.toLowerCase()} yok</p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                      {users.map((u) => (
                        <label
                          key={u.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedUsers.includes(u.id)
                              ? 'border-[#2563EB] bg-[#F0F7F4]'
                              : 'border-[#E5E5E5] hover:border-[#2563EB]'
                          }`}
                          data-testid={`user-checkbox-${u.id}`}
                        >
                          <Checkbox
                            checked={selectedUsers.includes(u.id)}
                            onCheckedChange={() => toggleUser(u.id)}
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#1A1A1A] truncate">{u.name}</p>
                            <p className="text-xs text-[#595959] truncate">{u.email}{u.phone ? ` | ${u.phone}` : ''}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-[#595959] mt-2">{selectedUsers.length} kişi seçildi</p>
                </CardContent>
              </Card>

              <Card className="border border-[#E5E5E5]" data-testid="message-form-card">
                <CardContent className="pt-6 space-y-4">
                  <div>
                    <Label className="text-sm text-[#595959]">Mesaj</Label>
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Mesajınızı yazın..."
                      rows={4}
                      data-testid="message-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sms-direct"
                      checked={sendSms}
                      onCheckedChange={setSendSms}
                      data-testid="send-sms-checkbox"
                    />
                    <Label htmlFor="sms-direct" className="text-sm text-[#595959] cursor-pointer">
                      SMS olarak da gönder (telefon numarası olan kişilere)
                    </Label>
                  </div>
                  <Button
                    onClick={handleSendMessage}
                    disabled={sending || !message.trim() || selectedUsers.length === 0}
                    className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2 w-full h-11"
                    data-testid="send-message-button"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? 'Gönderiliyor...' : `${selectedUsers.length} Kişiye Gönder`}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Announcement Tab */}
            <TabsContent value="announcement" className="space-y-4">
              <Card className="border border-[#E5E5E5]" data-testid="announcement-form-card">
                <CardHeader>
                  <CardTitle className="text-lg text-[#2563EB] flex items-center gap-2">
                    <Megaphone className="w-5 h-5" strokeWidth={1.5} />
                    Genel Duyuru
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-[#595959]">
                    Bu duyuru tüm {labels.people.toLowerCase()} ({users.length} kişi) gönderilecektir.
                  </p>
                  <div>
                    <Label className="text-sm text-[#595959]">Duyuru Mesajı</Label>
                    <Textarea
                      value={announcementMsg}
                      onChange={(e) => setAnnouncementMsg(e.target.value)}
                      placeholder="Duyuru mesajınızı yazın..."
                      rows={4}
                      data-testid="announcement-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="sms-announcement"
                      checked={sendSmsDuyuru}
                      onCheckedChange={setSendSmsDuyuru}
                      data-testid="send-sms-announcement-checkbox"
                    />
                    <Label htmlFor="sms-announcement" className="text-sm text-[#595959] cursor-pointer">
                      SMS olarak da gönder (telefon numarası olan kişilere)
                    </Label>
                  </div>
                  <Button
                    onClick={handleSendAnnouncement}
                    disabled={sending || !announcementMsg.trim()}
                    className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2 w-full h-11"
                    data-testid="send-announcement-button"
                  >
                    <Megaphone className="w-4 h-4" />
                    {sending ? 'Gönderiliyor...' : `Tüm ${labels.people} Duyuru Gönder`}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      
    </PageLayout>
  );
};
